import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Address, erc20Abi } from 'viem';
import { getBalance, readContract, readContracts } from '@wagmi/core';
import { useReadContracts } from 'wagmi';

import { config } from '@/core/wagmiConfig';
import { NETWORK_CHAIN, VOID_USER } from '@/core/config';
import { ABI as wstETHABI } from '@/abis/wstETH';
import { getVaults } from '@/features/vaults/utils/getVaults';
import { useVaultsCollectorData } from '@/features/vaults/hooks/useVaultsCollectorData';
import { RestakeSupportedNetwork } from '@/core/types/RestakeSupportedNetwork';
import { Network } from '@/shared/types/Network';
import { lidoEarnEth } from '@/features/vaults/constants/vaults/lido/lido-earn-eth';
import { mezoUSD, mUSDC, mUSDT } from '@/features/vaults/constants/vaults/mezo/tokens/mezoUSDs';
import { mezoBtcTokenAddress } from '@/features/vaults/constants/vaults/mezo/tokens/mezoBtcTokenAddress';
import { mezoCbBtcTokenAddress } from '@/features/vaults/constants/vaults/mezo/tokens/mezoCbBtcTokenAddress';

import { ethAddress, wstETHAddress, zeroAddress } from './config';
import { TokenData } from './types';

export const supportedTokens: Partial<
  Record<Network, Record<string, { symbol: string; decimals: number }>>
> = {
  ethereum: {
    '0xae7ab96520DE3A18E5e111B5EaAb095312D7fE84': { symbol: 'stETH', decimals: 18 },
    '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2': { symbol: 'wETH', decimals: 18 },
    '0x7f39C581F595B53c5cb19bD0b3f8dA6c935E2Ca0': { symbol: 'wstETH', decimals: 18 },
    [zeroAddress]: { symbol: 'ETH', decimals: 18 },
    [ethAddress]: { symbol: 'ETH', decimals: 18 },
    '0x9D39A5DE30e57443BfF2A8307A4256c8797A3497': { symbol: 'sUSDe', decimals: 18 },
    '0x57e114B691Db790C35207b2e685D4A43181e6061': { symbol: 'ENA', decimals: 18 },
    '0xC937e208aCd2Ea6126A3B7731C7c72f6E9307D1b': { symbol: 'wstETH', decimals: 18 },
    '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599': { symbol: 'wBTC', decimals: 8 },
    '0x18084fbA666a33d37592fA2633fD49a74DD93a88': { symbol: 'tBTC', decimals: 18 },
    '0x20157DBAbb84e3BBFE68C349d0d44E48AE7B5AD2': { symbol: 'IBTC', decimals: 8 },
    '0x004E9C3EF86bc1ca1f0bB5C7662861Ee93350568': { symbol: 'uniBTC', decimals: 8 },
    '0x57B946008913B82E4dF85f501cbAeD910e58D26C': { symbol: 'POND', decimals: 18 },
    '0x1202F5C7b4B9E47a1A484E8B270be34dbbC75055': { symbol: 'wstUSR', decimals: 18 },
    '0x6bedE1c6009a78c222D9BDb7974bb67847fdB68c': { symbol: 'USBD', decimals: 18 },
    '0xd38BB40815d2B0c2d2c866e0c72c5728ffC76dd9': { symbol: 'SIS', decimals: 18 },
    '0xfE0ccc9942E98C963Fe6b4e5194EB6e3Baa4cb64': { symbol: 'sYUSD', decimals: 18 },
    '0xf0DB65D17e30a966C2ae6A21f6BBA71cea6e9754': { symbol: 'BARD', decimals: 18 },
    '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48': { symbol: 'USDC', decimals: 6 },
    '0xdAC17F958D2ee523a2206206994597C13D831ec7': { symbol: 'USDT', decimals: 6 },
    '0xdC035D45d973E3EC169d2276DDab16f1e407384F': { symbol: 'USDS', decimals: 18 },
    '0xdde3eC717f220Fc6A29D6a4Be73F91DA5b718e55': { symbol: 'USDU', decimals: 18 },
    '0x4c9EDD5852cd905f086C759E8383e09bff1E68B3': { symbol: 'USDe', decimals: 18 },
    '0x5086bf358635B81D8C47C66d1C8b9E567Db70c72': { symbol: 'reUSD', decimals: 18 },
    '0xdD468A1DDc392dcdbEf6db6e34E89AA338F9F186': { symbol: 'MUSD', decimals: 18 },
    '0xcbB7C0000aB88B473b1f5aFd9ef808440eed33Bf': { symbol: 'cbBTC', decimals: 8 },
    ...lidoEarnEth.additionalDepositTokens?.reduce(
      (acc: Record<string, { symbol: string; decimals: number }>, el) => {
        acc[el.address] = { symbol: el.symbol, decimals: el.decimals };
        return acc;
      },
      {},
    ),
  },
  lisk: {
    [zeroAddress]: { symbol: 'ETH', decimals: 18 },
    [ethAddress]: { symbol: 'ETH', decimals: 18 },
    '0x76D8de471F54aAA87784119c60Df1bbFc852C415': { symbol: 'wstETH', decimals: 18 },
    '0x9BFA177621119e64CecbEabE184ab9993E2ef727': { symbol: 'M-BTC', decimals: 18 },
    '0xac485391EB2d7D88253a7F1eF18C37f4242D1A24': { symbol: 'LSK', decimals: 18 },
  },
  fraxtal: {
    [zeroAddress]: { symbol: 'FRAX', decimals: 18 },
    [ethAddress]: { symbol: 'FRAX', decimals: 18 },
    '0xFc00000000000000000000000000000000000002': { symbol: 'WFRAX', decimals: 18 },
  },
  base: {
    [zeroAddress]: { symbol: 'ETH', decimals: 18 },
    [ethAddress]: { symbol: 'ETH', decimals: 18 },
    '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913': { symbol: 'USDC', decimals: 6 },
    '0xfde4C96c8593536E31F229EA8f37b2ADa2699bb2': { symbol: 'USDT', decimals: 6 },
    '0x98d0baa52b2D063E780DE12F615f963Fe8537553': { symbol: 'KAITO', decimals: 18 },
    '0x4200000000000000000000000000000000000006': { symbol: 'WETH', decimals: 18 },
    '0x0b2558bdBC7FFEC0f327fB3579c23daBD1699706': { symbol: 'THQ', decimals: 18 },
  },
  holesky: {
    '0x94373a4919B3240D86eA41593D5eBa789FEF3848': { symbol: 'wETH', decimals: 18 },
    '0x8d09a4502Cc8Cf1547aD300E066060D043f6982D': { symbol: 'wstETH', decimals: 18 },
    '0x3F1c547b21f65e10480dE3ad8E19fAAC46C95034': { symbol: 'stETH', decimals: 18 },
    [zeroAddress]: { symbol: 'ETH', decimals: 18 },
    [ethAddress]: { symbol: 'ETH', decimals: 18 },
  },
  hoodi: {
    '0xdb7057418E9272216551a6eA07876a1e26D306f9': { symbol: 'wETH', decimals: 18 },
    '0x7E99eE3C66636DE415D2d7C880938F2f40f94De4': { symbol: 'wstETH', decimals: 18 },
    [zeroAddress]: { symbol: 'ETH', decimals: 18 },
    [ethAddress]: { symbol: 'ETH', decimals: 18 },
  },
  bsc: {
    [zeroAddress]: { symbol: 'BNB', decimals: 18 },
    [ethAddress]: { symbol: 'BNB', decimals: 18 },
    '0x5845684b49aEf79A5c0F887f50401C247dca7AC6': { symbol: 'CYC', decimals: 18 },
    '0xabE8E5CabE24Cb36df9540088fD7cE1175b9bc52': { symbol: 'SOLV', decimals: 18 },
  },
  monad: {
    [zeroAddress]: { symbol: 'MON', decimals: 18 },
    [ethAddress]: { symbol: 'MON', decimals: 18 },
    '0x3bd359C1119dA7Da1D913D1C4D2B7c461115433A': { symbol: 'WMON', decimals: 18 },
  },
  'monad-testnet': {
    [zeroAddress]: { symbol: 'MON', decimals: 18 },
    [ethAddress]: { symbol: 'MON', decimals: 18 },
    '0x760AfE86e5de5fa0Ee542fc7B7B713e1c5425701': { symbol: 'WMON', decimals: 18 },
    '0xcf5a6076cfa32686c0Df13aBaDa2b40dec133F1d': { symbol: 'WBTC', decimals: 8 },
    '0xf817257fed379853cDe0fa4F97AB987181B1E5Ea': { symbol: 'USDC', decimals: 6 },
    '0x88b8E2161DEDC77EF4ab7585569D2415a1C1055D': { symbol: 'USDT', decimals: 6 },
  },
  rootstock: {
    [zeroAddress]: { symbol: 'RBTC', decimals: 18 },
    [ethAddress]: { symbol: 'RBTC', decimals: 18 },
    '0x967F8799aF07dF1534d48A95a5C9FEBE92c53AE0': { symbol: 'WRBTC', decimals: 18 },
  },
  mezo: {
    [mezoUSD]: { symbol: 'mezoUSD', decimals: 18 },
    [mUSDC]: { symbol: 'mUSDC', decimals: 6 },
    [mUSDT]: { symbol: 'mUSDT', decimals: 6 },
    [mezoBtcTokenAddress]: { symbol: 'BTC', decimals: 18 },
    [mezoCbBtcTokenAddress]: { symbol: 'cbBTC', decimals: 8 },
  },
};

// similar tokens
export const tokensFallback: Partial<
  Record<
    Network,
    Record<
      string,
      { network: RestakeSupportedNetwork; address: string; symbol: string; decimals: number }
    >
  >
> = {
  ethereum: {
    [ethAddress]: {
      network: 'ethereum',
      address: zeroAddress,
      symbol: 'ETH',
      decimals: 18,
    },
    '0xC937e208aCd2Ea6126A3B7731C7c72f6E9307D1b': {
      network: 'ethereum',
      address: '0x7f39C581F595B53c5cb19bD0b3f8dA6c935E2Ca0',
      symbol: 'wstETH',
      decimals: 18,
    },
  },
  lisk: {
    '0x76D8de471F54aAA87784119c60Df1bbFc852C415': {
      network: 'ethereum',
      address: '0x7f39C581F595B53c5cb19bD0b3f8dA6c935E2Ca0',
      symbol: 'wstETH',
      decimals: 18,
    },
  },
};

export type SupportedToken = {
  network: RestakeSupportedNetwork;
  address: Address;
  symbol: string;
  decimals: number;
};

export function getTokens(networks: RestakeSupportedNetwork[]) {
  const tokens: SupportedToken[] = [];

  for (const network of networks) {
    const networkTokens = supportedTokens[network] || {};
    for (const address in networkTokens) {
      tokens.push({
        network,
        address: address as Address,
        symbol: networkTokens[address].symbol,
        decimals: networkTokens[address].decimals,
      });
    }
  }

  return tokens;
}

export function getWstETHByStETH(
  network: RestakeSupportedNetwork,
  amountD18 = BigInt(1 * 10 ** 18),
) {
  return readContract(config, {
    abi: wstETHABI,
    address: wstETHAddress[network]!,
    functionName: 'getWstETHByStETH',
    chainId: NETWORK_CHAIN[network],
    args: [amountD18],
  });
}

async function getTokensData(
  network: RestakeSupportedNetwork,
  tokens: Address[],
  userAddress: string,
) {
  const data: Record<string, TokenData | undefined> = {};

  const [tokensBalances, ethBalance] = await Promise.all([
    readContracts(config, {
      contracts: tokens.map(
        tokenAddress =>
          ({
            abi: erc20Abi,
            address: tokenAddress as Address,
            functionName: 'balanceOf',
            args: [userAddress as Address],
            chainId: NETWORK_CHAIN[network || ''],
          }) as const,
      ),
      allowFailure: true,
    }),
    getBalance(config, {
      address: userAddress as Address,
      chainId: NETWORK_CHAIN[network!],
    }),
  ]);

  for (let i = 0; i < tokens.length; i++) {
    const address = tokens[i];
    const token = supportedTokens[network]?.[address];
    const balance = tokensBalances[i];
    if (token && balance.status === 'success') {
      data[address] = {
        network,
        userBalance: Number(balance.result) / 10 ** token.decimals,
        userBalanceBN: balance.result,
        ...token,
        address,
        usdPrice: 0,
      };
    }
  }

  // ETH
  const ethToken = supportedTokens[network!]?.[zeroAddress];
  if (ethToken) {
    data[zeroAddress] = {
      ...ethToken,
      network: network!,
      userBalanceBN: ethBalance.value,
      address: zeroAddress,
      userBalance: userAddress ? Number(ethBalance.value) / 10 ** 18 : 0,
      usdPrice: 0,
    };
  }
  const ethAliasToken = supportedTokens[network!]?.[ethAddress];
  if (ethAliasToken) {
    data[ethAddress] = {
      ...ethAliasToken,
      network: network!,
      userBalanceBN: ethBalance.value,
      address: zeroAddress,
      userBalance: userAddress ? Number(ethBalance.value) / 10 ** 18 : 0,
      usdPrice: 0,
    };
  }

  return data;
}

export function useTokensData(
  networks: RestakeSupportedNetwork[],
  userAddress: string = VOID_USER.holesky,
) {
  const { data: vaultsCollectorData } = useVaultsCollectorData(networks, userAddress);

  const tokens = useMemo(() => {
    const tokensResult: Partial<Record<RestakeSupportedNetwork, Address[]>> = {};

    for (const network of networks) {
      const vaults = getVaults([network]);
      tokensResult[network] = getTokens([network]).map(token => token.address);

      for (const vault of vaults) {
        if (vault?.additionalTokens) {
          for (const token of vault.additionalTokens) {
            tokensResult[network]!.push(token as Address);
          }
        }

        if (vault?.depositTokens) {
          for (const token of vault.depositTokens) {
            tokensResult[network]!.push(token.address as Address);
          }
        }

        if (vault?.additionalDepositTokens) {
          for (const token of vault.additionalDepositTokens) {
            tokensResult[network]!.push(token.address as Address);
          }
        }
      }
    }

    for (const [network, networkVaultsData] of Object.entries(vaultsCollectorData)) {
      if (!networkVaultsData) {
        continue;
      }

      for (const vaultData of networkVaultsData) {
        if (!tokensResult[network as RestakeSupportedNetwork]) {
          tokensResult[network as RestakeSupportedNetwork] = [];
        }

        for (const underlyingToken of vaultData.underlyingTokens) {
          tokensResult[network as RestakeSupportedNetwork]!.push(underlyingToken as Address);
        }
      }
    }

    // remove duplicates
    for (const network of networks) {
      tokensResult[network] = Array.from(new Set(tokensResult[network] || []));
    }

    return tokensResult;
  }, [networks, vaultsCollectorData]);

  const queryKey = useMemo(
    () => ['restake', 'vaults', 'tokens', userAddress || '', networks, 'data'],
    [networks, userAddress],
  );

  const queryResult = useQuery({
    queryKey,
    queryFn: async () => {
      const tokensData = await Promise.all(
        networks.map(network => getTokensData(network, tokens[network] || [], userAddress)),
      );

      return Object.fromEntries(networks.map((network, i) => [network, tokensData[i]])) as Record<
        RestakeSupportedNetwork,
        Record<string, TokenData>
      >;
    },
    enabled: !!userAddress && !!vaultsCollectorData,
  });

  return { ...queryResult, queryKey };
}

export type AllowanceContract = {
  chainId: number;
  token: `0x${string}`;
  owner: `0x${string}`;
  spender: `0x${string}`;
};

export type AllowanceResult = AllowanceContract & { allowance: bigint | null };

export function useAllowances(contracts: AllowanceContract[]) {
  return useReadContracts({
    contracts: contracts.map(contract => ({
      chainId: contract.chainId,
      address: contract.token,
      abi: erc20Abi,
      functionName: 'allowance',
      args: [contract.owner, contract.spender],
    })),
    allowFailure: true,
    query: {
      enabled: contracts.length > 0,
      select: allowances =>
        allowances.map(
          (allowance, index) =>
            ({
              ...contracts[index],
              allowance: allowance.status !== 'success' ? null : (allowance.result as bigint),
            }) as AllowanceResult,
        ),
    },
  });
}
